/**
 * Lazy-loaded datetime components via next/dynamic.
 *
 * Import from this subpath in Next.js apps to defer the react-aria
 * calendar/datepicker weight (~400 KB parsed JS) out of the first-load bundle.
 * These components render null on the server (ssr: false) — safe for client-only
 * permit wizard forms.
 *
 * Non-component utilities (stringToTimeValue, timeValueToString, etc.) are NOT
 * available here; import those from `@offstreetparking/design-system/forms/datetime/utils`.
 */
import dynamic from "next/dynamic";
import type { DateInputProps } from "../DateInput/DateInput";
import type { FormDateInputProps } from "../DateInput/FormDateInput";
import type { DateRangeInputProps } from "../DateRangeInput/DateRangeInput";
import type { FormDateRangeInputProps } from "../DateRangeInput/FormDateRangeInput";
import type { DateTimeInputProps } from "../DateTimeInput/DateTimeInput";
import type { FormDateTimeInputProps } from "../DateTimeInput/FormDateTimeInput";
import type { DurationPickerProps } from "../DurationPicker/DurationPicker";
import type { BaseMonthInputProps } from "../MonthInput/BaseMonthInput";
import type { FormMonthInputProps } from "../MonthInput/FormMonthInput";
import type { MonthInputProps } from "../MonthInput/MonthInput";
import type { BaseTimeInputProps } from "../TimeInput/BaseTimeInput";
import type { FormTimeInputProps } from "../TimeInput/FormTimeInput";
import type { TimeInputProps } from "../TimeInput/TimeInput";
import type { FormWeekInputProps } from "../WeekInput/FormWeekInput";
import type { WeekInputProps } from "../WeekInput/WeekInput";

export const DateInput = dynamic<DateInputProps>(() => import("../DateInput/DateInput").then((m) => m.DateInput), {
	ssr: false,
});

export const FormDateInput = dynamic<FormDateInputProps>(
	() => import("../DateInput/FormDateInput").then((m) => m.FormDateInput),
	{ ssr: false },
);

export const DateRangeInput = dynamic<DateRangeInputProps>(
	() => import("../DateRangeInput/DateRangeInput").then((m) => m.DateRangeInput),
	{ ssr: false },
);

export const FormDateRangeInput = dynamic<FormDateRangeInputProps>(
	() => import("../DateRangeInput/FormDateRangeInput").then((m) => m.FormDateRangeInput),
	{ ssr: false },
);

export const DateTimeInput = dynamic<DateTimeInputProps>(
	() => import("../DateTimeInput/DateTimeInput").then((m) => m.DateTimeInput),
	{ ssr: false },
);

export const FormDateTimeInput = dynamic<FormDateTimeInputProps>(
	() => import("../DateTimeInput/FormDateTimeInput").then((m) => m.FormDateTimeInput),
	{ ssr: false },
);

export const DurationPicker = dynamic<DurationPickerProps>(
	() => import("../DurationPicker/DurationPicker").then((m) => m.DurationPicker),
	{ ssr: false },
);

export const BaseMonthInput = dynamic<BaseMonthInputProps>(
	() => import("../MonthInput/BaseMonthInput").then((m) => m.BaseMonthInput),
	{ ssr: false },
);

export const MonthInput = dynamic<MonthInputProps>(() => import("../MonthInput/MonthInput").then((m) => m.MonthInput), {
	ssr: false,
});

export const FormMonthInput = dynamic<FormMonthInputProps>(
	() => import("../MonthInput/FormMonthInput").then((m) => m.FormMonthInput),
	{ ssr: false },
);

export const BaseTimeInput = dynamic<BaseTimeInputProps>(
	() => import("../TimeInput/BaseTimeInput").then((m) => m.BaseTimeInput),
	{ ssr: false },
);

export const TimeInput = dynamic<TimeInputProps>(() => import("../TimeInput/TimeInput").then((m) => m.TimeInput), {
	ssr: false,
});

export const FormTimeInput = dynamic<FormTimeInputProps>(
	() => import("../TimeInput/FormTimeInput").then((m) => m.FormTimeInput),
	{ ssr: false },
);

export const WeekInput = dynamic<WeekInputProps>(() => import("../WeekInput/WeekInput").then((m) => m.WeekInput), {
	ssr: false,
});

export const FormWeekInput = dynamic<FormWeekInputProps>(
	() => import("../WeekInput/FormWeekInput").then((m) => m.FormWeekInput),
	{ ssr: false },
);
